import { Provider } from '@ai-sdk-tools/store';
import type { AuthProviderContext } from '@newfront-insurance/next-auth';
import type { Provider as ProvisionProvider } from '@newfront-insurance/react-provision';
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { UIMessageWithMetadata } from '../../../lib/types';
import { DEFAULT_NOVA_CHAT_COMPLETIONS_ENDPOINT } from '../lib/transport';

export { NovaProvider, useNovaWrapperState, type NovaProviderProps, type NovaProviderCallbacks } from './nova-provider';
interface NovaContextValue {
  userUuid: string;
  accountUuid?: string;
  accountName?: string;
  scopes?: string[];
  authProvider: ProvisionProvider<AuthProviderContext>;
  apiEndpoint: string;
  baseUrl?: string;
}

export type { NovaContextValue };

const NovaContext = createContext<NovaContextValue | undefined>(undefined);

interface NovaChatProviderProps {
  children: ReactNode;
  userUuid: string;
  accountUuid?: string;
  accountName?: string;
  scopes?: string[];
  authProvider: ProvisionProvider<AuthProviderContext>;
  apiEndpoint?: string;
  baseUrl?: string;
  initialMessages?: UIMessageWithMetadata[];
}

/**
 * Combined provider that wraps both ChatProvider (from @ai-sdk-tools/store)
 * and NovaContextProvider. This is the main entry point for the chat system.
 *
 * Simplified to merge both providers into one to reduce nesting.
 *
 * @example
 * ```tsx
 * <NovaChatProvider
 *   authProvider={authProvider}
 *   userUuid={userUuid}
 *   accountUuid={accountUuid}
 * >
 *   <ChatRoot chatUuid={chatUuid}>
 *     <Messages />
 *     <ChatInput />
 *   </ChatRoot>
 * </NovaChatProvider>
 * ```
 */
export function NovaChatProvider({
  children,
  userUuid,
  accountUuid,
  accountName,
  scopes,
  authProvider,
  apiEndpoint = DEFAULT_NOVA_CHAT_COMPLETIONS_ENDPOINT,
  baseUrl,
  initialMessages,
}: NovaChatProviderProps) {
  const novaContextValue = useMemo<NovaContextValue>(
    () => ({
      userUuid,
      accountUuid,
      accountName,
      scopes,
      authProvider,
      apiEndpoint,
      baseUrl,
    }),
    [userUuid, accountUuid, accountName, scopes, authProvider, apiEndpoint, baseUrl],
  );

  return (
    <Provider initialMessages={initialMessages}>
      <NovaContext.Provider value={novaContextValue}>{children}</NovaContext.Provider>
    </Provider>
  );
}

/**
 * Hook to access Nova context values.
 * @throws Error if used outside of NovaChatProvider
 */
export function useNovaContext(): NovaContextValue {
  const context = useContext(NovaContext);
  if (!context) {
    throw new Error('useNovaContext must be used within NovaChatProvider');
  }
  return context;
}

export function useNovaContextSafe(): NovaContextValue | undefined {
  return useContext(NovaContext);
}

export type { NovaChatProviderProps };
